/**
 * Main application layout with sidebar and header.
 * Wraps content with ModuleProvider for module-aware navigation.
 * Supports RTL languages by flipping sidebar position.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Grip } from 'lucide-react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { ModuleSwitcher } from './ModuleSwitcher';
import { useRoutePending } from '../routing/routePendingContext';
import { GlobalSearchModal } from '../../shared/components/global-search';
import { ModuleProvider, useModule } from '../../shared/contexts/ModuleContext';
import { useModuleName } from '../../shared/hooks/useModuleName';
import { PageContextProvider } from '../../shared/contexts/PageContext';
import { ChatLauncherProvider } from '../../shared/contexts/ChatLauncherContext';
import { AnnouncementsProvider } from '../../modules/software_factory/components/delivery/AnnouncementsProvider';
import { useDirection } from '../../shared/contexts/DirectionContext';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import { getSecondaryPanelBg } from '../../shared/utils/color';
import { isListedNavItem } from '../../shared/utils/navItemAccess';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../shared/hooks/usePageTitle';
import { useDynamicFavicon } from '../../shared/hooks/useDynamicFavicon';
import { useModuleAccent } from '../../shared/hooks/useModuleAccent';
import {
  MAIN_CONTENT_TOP_PADDING_CLASS,
  MAIN_CONTENT_HORIZONTAL_PADDING_CLASS,
} from '../../shared/styles/layout.constants';
import { PageCompanyProvider } from '../../shared/contexts/PageCompanyContext';
import { PageCompanyIndicator } from '../../shared/components/multi-company';

// Custom hook for mobile detection using CSS media query
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

/** Sets module-level fallback title, favicon color and accent hue. Must be inside ModuleProvider. */
function ModuleBranding() {
  const { currentModule } = useModule();
  usePageTitle();
  // Publishes `--accent-module` on the document root — deliberately not on
  // DesktopLayoutWrapper, so portalled overlays inherit it too. Rendered here
  // because ModuleBranding is the one branding hook shared by the mobile and
  // desktop layout branches below.
  useModuleAccent();
  // Home module shows original favicon; other modules get icon-based favicon
  useDynamicFavicon(
    currentModule.id === 'home' ? undefined : currentModule.iconName,
    currentModule.id === 'home' ? undefined : currentModule.color,
  );
  return null;
}

/** Main content area with module-aware sidebar offset. Must be inside ModuleProvider. */
function MainContent({ isMobile }: { isMobile: boolean }) {
  // The outgoing page stays mounted and interactive-looking while the incoming
  // route's chunk loads (see RouteTransitionIndicator), so the landmark holding
  // it says outright that its content is stale. This works from inside the route
  // tree even though the transition is what is suspended: the pending flag
  // travels by context from ABOVE the router, at urgent priority.
  const routePending = useRoutePending();

  return (
    <main
      aria-busy={routePending}
      className={cn(
        'pb-6',
        MAIN_CONTENT_HORIZONTAL_PADDING_CLASS,
        // Mobile: fixed header needs top padding offset; Desktop: header is in flow
        isMobile ? cn('min-h-screen', MAIN_CONTENT_TOP_PADDING_CLASS) : 'pt-4'
      )}
    >
      {/*
        The mobile arm of the app-bar company slot. The header's own slot is
        `hidden sm:flex` because ui-navigation.md § "Mobile header composition"
        fixes the 375px icon cluster and a company chip is not in it — so at
        mobile widths the record's company renders here, at the top of the
        content, still above the fold.

        The two are exact CSS complements (`hidden sm:flex` / `sm:hidden`), NOT
        a JS breakpoint on one side and a media query on the other: that pair
        disagrees at the boundary and renders the control twice or not at all.
        Exactly one company control at every width.
      */}
      <PageCompanyIndicator className="mb-3 sm:hidden" testId="page-company-indicator-mobile" />
      <Outlet />
    </main>
  );
}

/** Desktop layout wrapper — uses sidebar background color for the outer shell. Must be inside ModuleProvider. */
function DesktopLayoutWrapper({ children }: { children: React.ReactNode }) {
  const { currentModule } = useModule();
  const { resolvedTheme } = useThemeMode();
  const isDarkMode = resolvedTheme === 'dark';
  const panelBg = getSecondaryPanelBg(currentModule.color, isDarkMode);

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ backgroundColor: panelBg }}
    >
      {children}
    </div>
  );
}

/**
 * Desktop sidebar — hidden only when the current module has no LISTED nav
 * items (e.g. Home). `searchOnly` items are search-index entries, never rows,
 * so a module holding nothing else still has an empty sidebar and must keep
 * the inline module switcher instead of an empty panel.
 */
function DesktopSidebar({ isRtl, collapsed }: { isRtl: boolean; collapsed: boolean }) {
  const { currentModule } = useModule();
  if (!currentModule.navItems.some(isListedNavItem)) return null;
  return <Sidebar open variant="permanent" anchor={isRtl ? 'right' : 'left'} collapsed={collapsed} />;
}

/** Returns true when sidebar is not visible (no listed nav items). Must be inside ModuleProvider. */
function useIsSidebarHidden() {
  const { currentModule } = useModule();
  return !currentModule.navItems.some(isListedNavItem);
}

/** Inline module switcher shown when sidebar is hidden. Must be inside ModuleProvider. */
function InlineModuleSwitcher() {
  const { t } = useTranslation('common');
  const { currentModule } = useModule();
  const getModuleName = useModuleName();
  const ModuleIcon = currentModule.icon as React.ElementType;

  return (
    <Tooltip>
      <ModuleSwitcher
        trigger={
          <TooltipTrigger asChild>
            <button
              className="flex items-center gap-2 h-8 touch:min-h-[44px] pl-1.5 pr-2 rounded-lg hover:bg-accent transition-colors"
              aria-label={t('modules.switchModule')}
              data-testid="module-switcher"
            >
              <Grip className="h-5 w-5 text-foreground" />
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center"
                style={{ backgroundColor: currentModule.color }}
              >
                {typeof ModuleIcon === 'function' ? (
                  <ModuleIcon className="h-3.5 w-3.5 text-white" />
                ) : (
                  <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 text-white">
                    {currentModule.icon}
                  </span>
                )}
              </div>
              <span className="font-medium text-sm">
                {getModuleName(currentModule)}
              </span>
            </button>
          </TooltipTrigger>
        }
        align="start"
      />
      <TooltipContent side="bottom">{t('modules.switchModule')}</TooltipContent>
    </Tooltip>
  );
}

/** Desktop toolbar above content — toggle button + optional module switcher + header. Must be inside ModuleProvider. */
function DesktopContentToolbar({
  sidebarCollapsed,
  onToggleSidebar,
  onMenuClick,
  onSearchClick,
  searchOpen,
  onSearchOpenChange,
  isRtl,
}: {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onMenuClick: () => void;
  onSearchClick: () => void;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  isRtl: boolean;
}) {
  const { t } = useTranslation('common');
  const sidebarHidden = useIsSidebarHidden();

  // Arrow icon points in the direction the sidebar will move when toggled.
  // LTR: sidebar on the left → "close" arrow points left, "open" arrow points right.
  // RTL: sidebar on the right → flip to PanelRight variants so arrows still point toward the sidebar.
  const ToggleIcon = isRtl
    ? (sidebarCollapsed ? PanelRightOpen : PanelRightClose)
    : (sidebarCollapsed ? PanelLeftOpen : PanelLeftClose);

  return (
    <div className="flex items-center shrink-0">
      {sidebarHidden && <div className="ml-2 mt-1"><InlineModuleSwitcher /></div>}
      {!sidebarHidden && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleSidebar}
              className="h-8 w-8 mt-1 ml-2 text-muted-foreground hover:text-foreground"
              aria-label={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
              aria-expanded={!sidebarCollapsed}
            >
              <ToggleIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          </TooltipContent>
        </Tooltip>
      )}
      <div className="flex-1">
        <Header onMenuClick={onMenuClick} onSearchClick={onSearchClick} searchOpen={searchOpen} onSearchOpenChange={onSearchOpenChange} showMenuButton={false} isRtl={isRtl} />
      </div>
    </div>
  );
}

export function MainLayout() {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('sidebar-collapsed') === 'true'; } catch { return false; }
  });
  const { isRtl } = useDirection();

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearchOpen = useCallback(() => {
    setSearchOpen(true);
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      // Cmd+B / Ctrl+B — toggle sidebar (unless in a text input where it means bold)
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        const el = document.activeElement;
        const isTextInput = el instanceof HTMLTextAreaElement ||
          el instanceof HTMLInputElement ||
          (el instanceof HTMLElement && el.isContentEditable);
        if (!isTextInput) {
          e.preventDefault();
          toggleSidebar();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggleSidebar]);

  return (
    <ModuleProvider>
    <PageContextProvider>
      <PageCompanyProvider>
    <ChatLauncherProvider>
    <AnnouncementsProvider>
      <ModuleBranding />
      {/* Modal search only on mobile; desktop uses inline search in header */}
      {isMobile && <GlobalSearchModal open={searchOpen} onOpenChange={setSearchOpen} />}
      {isMobile ? (
        /* Mobile: stacked layout (unchanged) */
        <div className="min-h-screen bg-background">
          <Sidebar
            open={mobileOpen}
            onClose={handleDrawerToggle}
            variant="temporary"
            anchor={isRtl ? 'right' : 'left'}
          />
          <Header onMenuClick={handleDrawerToggle} onSearchClick={handleSearchOpen} showMenuButton isRtl={isRtl} />
          <MainContent isMobile />
        </div>
      ) : (
        /* Desktop: sidebar | rounded content area */
        <DesktopLayoutWrapper>
          {/* Sidebar — collapsible */}
          <DesktopSidebar isRtl={isRtl} collapsed={sidebarCollapsed} />

          {/* Content area — rounded, with gap from sidebar and screen edges */}
          <div className={cn(
            'flex-1 flex flex-col min-w-0 overflow-hidden',
            'bg-background rounded-xl',
            // 8px — mirrored by DESKTOP_FRAME_INSET in shared/styles/layout.constants,
            // which fixed overlays use to find the header's real bottom edge.
            'mt-2 mr-2 mb-2 ml-2',
          )}>
            <DesktopContentToolbar
              sidebarCollapsed={sidebarCollapsed}
              onToggleSidebar={toggleSidebar}
              onMenuClick={handleDrawerToggle}
              onSearchClick={handleSearchOpen}
              searchOpen={searchOpen}
              onSearchOpenChange={setSearchOpen}
              isRtl={isRtl}
            />
            <div
              className="flex-1 overflow-auto focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
              tabIndex={0}
              role="region"
              aria-label={t('nav.mainContent')}
            >
              <MainContent isMobile={false} />
            </div>
          </div>
        </DesktopLayoutWrapper>
      )}
    </AnnouncementsProvider>
    </ChatLauncherProvider>
      </PageCompanyProvider>
    </PageContextProvider>
    </ModuleProvider>
  );
}
